import { Component, Inject, Vue } from 'vue-property-decorator';
import PersonaService from '@/shared/persona/persona.service';
import { Ayudantia, EstadoVigencia, Expediente, HistoricoPersonasApi, VigenciaSni } from '@conacyt/historicopersonasms-client';
import { IPersonaFisica, PersonaFisica } from '@conacyt/buscador-cvu';
import * as personaUtils from '@/shared/util/format-util';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import * as dateUtils from '@/shared/date/date-utils';
import { TipoVigencia } from '@/shared/model/enumerations/tipo-vigencia.model';

@Component
export default class BuscadorPersonaDualComponent extends Vue {
  @Inject('personaService') private readonly personaService: () => PersonaService;
  @Inject('historicoPersonasApi') private readonly historicoPersonasApi: () => HistoricoPersonasApi;

  public perfilRizoma: IPersonaFisica = new PersonaFisica();

  public expediente: IPersonaFisica = new PersonaFisica();

  public expedienteCompleto: Expediente;

  cvuToSearch = null;
  isSearchingPerfil = false;
  isSearchingExpediente = false;
  perfilNotFound = true;
  expedienteNotFound = true;
  cvuTyped = null;
  findByName = false;

  public vigenciaActiva: VigenciaSni;
  public ultimaVigencia: VigenciaSni;
  public ayudantiaActiva: Ayudantia;
  public ultimaAyudantia: Ayudantia;

  public handleFindPersonaByCvu() {
    this.wipe();
    this.cvuTyped = this.cvuToSearch;
    this.buscarCvuPerfilRizoma();
    this.buscarCvuExpedientePersona();
    this.getInformacionGeneral();
  }

  /**
   * Consulta la información de un CVU en Rizoma/Perfil.
   */
  private buscarCvuPerfilRizoma() {
    this.isSearchingPerfil = true;
    this.personaService()
      .findByCvu(this.cvuToSearch)
      .then(res => {
        if (res) {
          this.perfilRizoma = res;
          this.perfilNotFound = false;
        } else {
          this.perfilNotFound = true;
        }
        this.isSearchingPerfil = false;
      })
      .catch(() => {
        this.isSearchingPerfil = false;
        this.perfilNotFound = true;
      })
      .finally(() => {
        this.cvuToSearch = null;
        this.getInformacionGeneral();
      });
  }

  /**
   * Consulta la información de un CVU en Historico-Persona.
   */
  private buscarCvuExpedientePersona() {
    this.isSearchingExpediente = true;
    this.limpiarObjetos();
    this.historicoPersonasApi()
      .getExpediente(this.cvuToSearch)
      .then(res => {
        if (res) {
          this.expediente = res.data.datosGenerales.persona;
          this.expedienteNotFound = false;
          this.expedienteCompleto = res.data;
          if (res?.data?.ayudantias?.length > 0) {
            this.getAyudantiaActiva();
            this.getUltimaAyudantia();
          }
          if (res?.data?.vigenciasSni?.length > 0) {
            this.getVigenciaActiva();
            this.getUltimaVigencia();
          }
        } else {
          this.expedienteNotFound = true;
        }
        this.isSearchingExpediente = false;
      })
      .catch(() => {
        // this.isSearchingExpediente = false;
        this.expedienteNotFound = true;
      })
      .finally(() => {
        this.isSearchingExpediente = false;
        this.cvuToSearch = null;
        this.getInformacionGeneral();
      });
  }

  private limpiarObjetos() {
    this.vigenciaActiva = null;
    this.ultimaVigencia = null;
    this.ayudantiaActiva = null;
    this.ultimaAyudantia = null;
  }

  public nombreBuscar = '';
  public primerApellidoBuscar = '';
  public segundoApellidoBuscar = '';

  public expedientesEncontrados = [];

  public buscarNameExpedientePersona() {
    this.isSearchingExpediente = true;

    this.historicoPersonasApi()
      .getExpedienteNombreApellidos(this.nombreBuscar, this.primerApellidoBuscar)
      .then(res => {
        if (res) {
          this.expedientesEncontrados = res.data;
          // this.expediente = res.da;
          // this.expedienteNotFound = false;
          // this.expedienteCompleto = res.data;
          // this.getVigenciaActiva();
          // this.getUltimaVigencia();
          // this.getAyudantiaActiva();
          // this.getUltimaAyudantia();
        } else {
          // this.expedienteNotFound = true;
        }
        // this.isSearchingExpediente = false;
      })
      .catch(() => {
        // this.isSearchingExpediente = false;
        // this.expedienteNotFound = true;
      })
      .finally(() => {
        this.isSearchingExpediente = false;
        this.cvuToSearch = null;
        // this.getInformacionGeneral();
      });
  }
  /**
   * Valida el campo de búsqueda de CVU.
   */
  get isSearchDisabled(): boolean {
    return this.isSearchingPerfil || this.isSearchingExpediente || !this.cvuToSearch?.length;
  }

  /**
   * Reinicializa los valores de las consultas.
   */
  public wipe() {
    this.expediente = new PersonaFisica();
    this.perfilRizoma = new PersonaFisica();
  }

  /**
   * Concatena el nombre completo de la persona.
   *
   * @param persona
   * @returns
   */
  public getFullName(persona: { nombre?: string; apellidoPaterno?: string; apellidoMaterno?: string }): string {
    const fullName = [persona.nombre, persona.apellidoPaterno, persona.apellidoMaterno].filter(Boolean).join(' ');
    return fullName || 'N/A';
  }

  /**
   * Auto selecciona la información dependiendo el origen
   */
  private getInformacionGeneral() {
    if (this?.expediente?.cvu) {
      this.$emit('selected', this.expediente);
    } else if (this?.perfilRizoma?.cvu) {
      this.$emit('selected', this.perfilRizoma);
    } else {
      this.$emit('selected', null);
    }
  }

  /**
   * Llama a función que permite solo capturar números.
   * @param evt
   */
  public onlyNumbers(evt): void {
    personaUtils.onlyNumbersV2(evt);
  }

  /**
   * Llama a función que permite solo capturar letras.
   * @param evt
   */
  public onlyLetters(evt): void {
    personaUtils.onlyLettersSingleSpace(evt);
  }

  public getVigenciaActiva(): VigenciaSni {
    return (this.vigenciaActiva = this.expedienteCompleto?.vigenciasSni?.find(
      v => v.activo === true && v.estatusVigencia.clave === EstadoVigencia.ACTIVO && v.tipo.clave !== '8'
    ));
  }

  private getUltimaVigencia2(): VigenciaSni {
    return (this.ultimaVigencia = this.expedienteCompleto?.vigenciasSni
      .filter(v => v.tipo.clave == '8' || (v.activo === false && v.estatusVigencia.clave == EstadoVigencia.INACTIVO))
      .reduce((latest, curr) => {
        return !latest || new Date(curr.vigencia?.fechaFin) > new Date(latest.vigencia?.fechaFin) ? curr : latest;
      }, null as any));
  }

  private getUltimaVigencia(): VigenciaSni | null {
    const vigencias = this.expedienteCompleto?.vigenciasSni ?? [];

    // 1. If clave 8 exists, return it immediately
    const clave8 = vigencias.find(v => v.tipo?.clave === '8');
    if (clave8) {
      return (this.ultimaVigencia = clave8);
    }

    // 2. Otherwise, filter and get the latest by fechaFin
    return (this.ultimaVigencia = vigencias
      .filter(v => v.activo === false && v.estatusVigencia?.clave === EstadoVigencia.INACTIVO)
      .reduce((latest, curr) => {
        if (!latest) return curr;

        const currFecha = new Date(curr.vigencia?.fechaFin);
        const latestFecha = new Date(latest.vigencia?.fechaFin);

        return currFecha > latestFecha ? curr : latest;
      }, null as any));
  }

  private getAyudantiaActiva(): Ayudantia {
    return (this.ayudantiaActiva = this.expedienteCompleto?.ayudantias?.find(a => {
      const fechaFin = new Date(a.vigencia?.fechaFin);
      return !isNaN(fechaFin.getTime()) && fechaFin >= new Date() && a.estadoSolicitud === EstadoSolicitud.FORMALIZADA;
    }));
  }

  private getUltimaAyudantia(): Ayudantia {
    const hoy = new Date();

    const finalizadas = this.expedienteCompleto?.ayudantias?.filter(a => new Date(a.vigencia.fechaFin) < hoy);

    if (!finalizadas || finalizadas.length === 0) return null;

    return (this.ultimaAyudantia = finalizadas.reduce((latest, curr) =>
      new Date(curr.vigencia.fechaFin) > new Date(latest.vigencia.fechaFin) ? curr : latest
    ));
  }

  public formatDate(fecha) {
    if (fecha != null) {
      return dateUtils.formatUtc(fecha);
    }
    return null;
  }

  formatLettersAndSpace(value: string): string {
    let val = value.toUpperCase();
    val = val.replace(/\s+/g, ' '); // single space only
    return val;
  }
}
